import { useRef, useCallback, useEffect } from 'react';
import { flushSync } from 'react-dom';

// ===== HOOK: Pull-down-to-close + Pull-up-to-action (finger-tracking) =====
//
// Animation architecture:
//   - During gesture/animation: JS inline styles drive all elements
//   - Panel uses position:absolute + bottom:0 during animation (rises from viewport bottom)
//   - Slide padding, image border-radius & box-shadow are also animated for smooth morph
//   - On completion: flushSync(setPanel) guarantees CSS class is in DOM before inline styles are cleared
//   - CSS class .inlineActionPanelOpen also uses position:absolute (matching animation end state)
//     → zero-jump handoff between JS inline and CSS class
//   - transitionend + setTimeout fallback for reliable completion detection

export function usePullGestures(
  onClose: () => void,
  setPanel: React.Dispatch<React.SetStateAction<boolean>>,
  enabled: boolean,
  actionPanelOpen: boolean,
  panelRef: React.RefObject<HTMLDivElement | null>,
  carouselExtraRef: React.MutableRefObject<HTMLDivElement | null>,
  imageContainerRef: React.RefObject<HTMLDivElement | null>,
) {
  const carouselRef = useRef<HTMLDivElement>(null);
  const dragStart = useRef<{ x: number; y: number } | null>(null);
  const dragDelta = useRef(0);
  const dragging = useRef(false);
  const decided = useRef(false);
  const direction = useRef<'down' | 'up' | null>(null);
  const inScrollable = useRef(false);
  const busy = useRef(false);

  // Capture image height before cropping (for open gesture tracking)
  const imgStartH = useRef(0);
  // Capture original box-shadow for close animation restore
  const origShadow = useRef('none');
  // Capture original slide padding for close animation restore
  const origPadTop = useRef(0);
  const origPadLR = useRef(0);

  const CLOSE_TH = 100;
  const PANEL_TH = 60;
  const DIR_TH = 10;
  const DUR = 300;
  const EASE = 'ease';
  const SIDE_MARGIN = 16; // --gallery-spacing-lg
  const RADIUS = 16;      // --gallery-radius-xl
  const IMG_RADIUS = 16;  // --gallery-radius-xl for <img>

  // ── Helpers ──

  const findScroller = useCallback((t: EventTarget | null): HTMLElement | null => {
    if (!t || !(t instanceof HTMLElement)) return null;
    let el: HTMLElement | null = t;
    while (el && el !== carouselRef.current) {
      if (el.scrollHeight > el.clientHeight + 2) {
        const ov = getComputedStyle(el).overflowY;
        if (ov === 'auto' || ov === 'scroll') return el;
      }
      el = el.parentElement;
    }
    return null;
  }, []);

  /** 21:9 image height based on viewport width */
  const getTargetH = useCallback(() => Math.max(120, Math.round(window.innerWidth * 9 / 21)), []);

  /** Top margin for cropped image (matches CSS: --page-padding-top + 8px) */
  const getTopMargin = useCallback(() => {
    const ppTop = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--page-padding-top')) || 16;
    return ppTop + 8;
  }, []);

  /** Full image height when panel is closed (viewport − thumbnails − spacer) */
  const getFullImgH = useCallback(() => window.innerHeight - 72 - 88, []);

  /** Panel target height (viewport − image − margins) */
  const getPanelTargetH = useCallback(() => {
    return window.innerHeight - getTargetH() - getTopMargin() - SIDE_MARGIN;
  }, [getTargetH, getTopMargin]);

  /** Query thumbnail strip and spacer from carousel DOM */
  const getExtras = useCallback(() => ({
    ts: carouselRef.current?.querySelector('[data-role="thumb-strip"]') as HTMLElement | null,
    sp: carouselRef.current?.querySelector('[data-role="spacer"]') as HTMLElement | null,
  }), []);

  /** Query active swiper slide and its <img> elements */
  const getSlideInner = useCallback(() => {
    const container = imageContainerRef.current;
    if (!container) return { slide: null as HTMLElement | null, pics: [] as HTMLElement[] };
    const slide = container.querySelector('.swiper-slide-active') as HTMLElement | null;
    const pics = slide ? (Array.from(slide.querySelectorAll('img')) as HTMLElement[]) : [];
    return { slide, pics };
  }, [imageContainerRef]);

  /** Clear all inline animation styles from main elements */
  const clearAll = useCallback((
    p: HTMLElement | null,
    img: HTMLElement | null,
    ts: HTMLElement | null,
    sp: HTMLElement | null,
    c: HTMLElement | null,
  ) => {
    if (p) {
      p.style.transition = '';
      p.style.position = '';
      p.style.top = '';
      p.style.bottom = '';
      p.style.left = '';
      p.style.right = '';
      p.style.height = '';
      p.style.maxHeight = '';
      p.style.opacity = '';
      p.style.overflow = '';
      p.style.willChange = '';
    }
    if (img) {
      img.style.transition = '';
      img.style.height = '';
      img.style.flex = '';
      img.style.marginTop = '';
      img.style.marginBottom = '';
      img.style.marginLeft = '';
      img.style.marginRight = '';
      img.style.width = '';
      img.style.borderRadius = '';
      img.style.willChange = '';
    }
    if (ts) {
      ts.style.transition = '';
      ts.style.height = '';
      ts.style.minHeight = '';
      ts.style.opacity = '';
      ts.style.overflow = '';
    }
    if (sp) {
      sp.style.transition = '';
      sp.style.height = '';
      sp.style.overflow = '';
    }
    if (c) {
      c.style.transition = '';
      c.style.transform = '';
      c.style.opacity = '';
    }
  }, []);

  /** Clear inline styles from slide and inner images */
  const clearSlideInner = useCallback((slide: HTMLElement | null, pics: HTMLElement[]) => {
    if (slide) {
      slide.style.transition = '';
      slide.style.padding = '';
      slide.style.paddingTop = '';
      slide.style.paddingLeft = '';
      slide.style.paddingRight = '';
    }
    pics.forEach(pic => {
      pic.style.transition = '';
      pic.style.borderRadius = '';
      pic.style.boxShadow = '';
    });
  }, []);

  /** Attach transitionend + timeout fallback, call `cb` exactly once */
  const onceTransitionEnd = useCallback((
    el: HTMLElement,
    prop: string,
    duration: number,
    cb: () => void,
  ) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      el.removeEventListener('transitionend', handler);
      clearTimeout(timer);
      cb();
    };
    const handler = (e: TransitionEvent) => {
      if (e.target === el && e.propertyName === prop) finish();
    };
    el.addEventListener('transitionend', handler);
    const timer = setTimeout(finish, duration + 50);
  }, []);

  // ── Image container style helpers ──

  const IMG_TRANSITION = [
    `height ${DUR}ms ${EASE}`,
    `width ${DUR}ms ${EASE}`,
    `margin ${DUR}ms ${EASE}`,
    `border-radius ${DUR}ms ${EASE}`,
  ].join(', ');

  const INNER_TRANSITION = `border-radius ${DUR}ms ${EASE}, box-shadow ${DUR}ms ${EASE}`;
  const SLIDE_TRANSITION = `padding ${DUR}ms ${EASE}`;

  /** Set image container to a given visual state (no transition) */
  const setImgState = useCallback((
    img: HTMLElement,
    h: number, w: number,
    mt: number, mb: number, ml: number, mr: number,
    radius: number,
  ) => {
    img.style.flex = 'none';
    img.style.height = h + 'px';
    img.style.width = w + 'px';
    img.style.marginTop = mt + 'px';
    img.style.marginBottom = mb + 'px';
    img.style.marginLeft = ml + 'px';
    img.style.marginRight = mr + 'px';
    img.style.borderRadius = radius + 'px';
  }, []);

  /** Read current computed image container state */
  const readImgState = useCallback((img: HTMLElement) => {
    const cs = getComputedStyle(img);
    return {
      h: img.offsetHeight,
      w: img.offsetWidth,
      mt: parseFloat(cs.marginTop) || 0,
      mb: parseFloat(cs.marginBottom) || 0,
      ml: parseFloat(cs.marginLeft) || 0,
      mr: parseFloat(cs.marginRight) || 0,
      radius: parseFloat(cs.borderRadius) || 0,
    };
  }, []);

  /** Set slide inner images to a given morph state (no transition) */
  const setInnerState = useCallback((
    slide: HTMLElement | null,
    pics: HTMLElement[],
    padTop: number,
    padLR: number,
    imgRadius: number,
    shadow: string,
  ) => {
    if (slide) {
      slide.style.paddingTop = padTop + 'px';
      slide.style.paddingLeft = padLR + 'px';
      slide.style.paddingRight = padLR + 'px';
    }
    pics.forEach(pic => {
      pic.style.borderRadius = imgRadius + 'px';
      pic.style.boxShadow = shadow;
    });
  }, []);

  // ── animateOpen ──
  const animateOpen = useCallback(() => {
    if (busy.current) return;
    busy.current = true;

    const p = panelRef.current;
    const img = imageContainerRef.current;
    const { ts, sp } = getExtras();
    const { slide, pics } = getSlideInner();
    const containerW = carouselRef.current?.offsetWidth ?? window.innerWidth;

    const targetH = getTargetH();
    const topM = getTopMargin();
    const bottomM = SIDE_MARGIN;
    const panelTargetH = getPanelTargetH();
    const targetW = containerW - SIDE_MARGIN * 2;

    if (!img) { busy.current = false; return; }

    // Capture current state (handles both fresh open and continuing from drag)
    const cur = readImgState(img);
    const curPanelH = p ? p.offsetHeight : 0;
    const curPanelOpacity = p ? (parseFloat(p.style.opacity) || 0) : 0;

    // Capture original slide/image values for close animation
    if (slide) {
      const scs = getComputedStyle(slide);
      origPadTop.current = parseFloat(scs.paddingTop) || 0;
      origPadLR.current = parseFloat(scs.paddingLeft) || 0;
    }
    if (pics.length) {
      origShadow.current = getComputedStyle(pics[0]).boxShadow || 'none';
    }

    // Read current slide inner state
    const curPadTop = slide ? (parseFloat(slide.style.paddingTop) || parseFloat(getComputedStyle(slide).paddingTop) || 0) : 0;
    const curPadLR = slide ? (parseFloat(slide.style.paddingLeft) || parseFloat(getComputedStyle(slide).paddingLeft) || 0) : 0;
    const curImgRadius = pics.length ? (parseFloat(pics[0].style.borderRadius) || parseFloat(getComputedStyle(pics[0]).borderRadius) || 0) : 0;
    const curImgShadow = pics.length ? (pics[0].style.boxShadow || getComputedStyle(pics[0]).boxShadow || 'none') : 'none';

    // ── Phase 1: Lock ALL current values (transition: none) ──
    img.style.willChange = 'height, width, margin, border-radius';
    img.style.transition = 'none';
    setImgState(img, cur.h, cur.w, cur.mt, cur.mb, cur.ml, cur.mr, cur.radius);

    if (slide) {
      slide.style.transition = 'none';
      slide.style.paddingTop = curPadTop + 'px';
      slide.style.paddingLeft = curPadLR + 'px';
      slide.style.paddingRight = curPadLR + 'px';
    }
    pics.forEach(pic => {
      pic.style.transition = 'none';
      pic.style.borderRadius = curImgRadius + 'px';
      pic.style.boxShadow = curImgShadow;
    });

    if (p) {
      p.style.position = 'absolute';
      p.style.bottom = '0';
      p.style.left = '0';
      p.style.right = '0';
      p.style.top = 'auto';
      p.style.maxHeight = 'none';
      p.style.height = curPanelH + 'px';
      p.style.opacity = String(curPanelOpacity);
      p.style.overflow = 'hidden';
      p.style.willChange = 'height, opacity';
      p.style.transition = 'none';
    }

    if (ts) {
      ts.style.overflow = 'hidden';
      ts.style.minHeight = '0';
      ts.style.transition = 'none';
    }
    if (sp) {
      sp.style.transition = 'none';
    }

    // ── Phase 2: Single reflow — all current values are committed ──
    void img.offsetHeight;

    // ── Phase 3: Set ALL transitions + ALL target values (same frame) ──
    img.style.transition = IMG_TRANSITION;
    setImgState(img, targetH, targetW, topM, bottomM, SIDE_MARGIN, SIDE_MARGIN, RADIUS);

    if (slide) {
      slide.style.transition = SLIDE_TRANSITION;
      slide.style.paddingTop = '0px';
      slide.style.paddingLeft = '0px';
      slide.style.paddingRight = '0px';
    }
    pics.forEach(pic => {
      pic.style.transition = INNER_TRANSITION;
      pic.style.borderRadius = '0px';
      pic.style.boxShadow = 'none';
    });

    if (p) {
      p.style.transition = `height ${DUR}ms ${EASE}, opacity ${DUR}ms ${EASE}`;
      p.style.height = panelTargetH + 'px';
      p.style.opacity = '1';
    }

    if (ts) {
      ts.style.transition = `height ${DUR}ms ${EASE}, opacity 200ms ${EASE}`;
      ts.style.height = '0px';
      ts.style.opacity = '0';
    }
    if (sp) {
      sp.style.transition = `height ${DUR}ms ${EASE}`;
      sp.style.height = '0px';
    }

    // 6. Completion: transitionend on image height + fallback timeout
    onceTransitionEnd(img, 'height', DUR, () => {
      flushSync(() => setPanel(true));
      clearAll(p, img, ts, sp, null);
      clearSlideInner(slide, pics);
      busy.current = false;
    });
  }, [panelRef, imageContainerRef, setPanel, getTargetH, getTopMargin, getPanelTargetH, getExtras, getSlideInner, clearAll, clearSlideInner, onceTransitionEnd, readImgState, setImgState, setInnerState, IMG_TRANSITION, INNER_TRANSITION, SLIDE_TRANSITION]);

  // ── animateClose ──
  const animateClose = useCallback(() => {
    if (busy.current) return;
    busy.current = true;

    const p = panelRef.current;
    const img = imageContainerRef.current;
    const c = carouselExtraRef.current;
    const { ts, sp } = getExtras();
    const { slide, pics } = getSlideInner();
    const containerW = carouselRef.current?.offsetWidth ?? window.innerWidth;
    const fullH = getFullImgH();

    if (!img) { busy.current = false; return; }

    // Capture current computed state
    const cur = readImgState(img);
    const curPanelH = p ? p.offsetHeight : 0;

    // Read current slide inner state (cropped: padding 0, radius 0, shadow none)
    const curPadTop = slide ? (parseFloat(getComputedStyle(slide).paddingTop) || 0) : 0;
    const curPadLR = slide ? (parseFloat(getComputedStyle(slide).paddingLeft) || 0) : 0;
    const curImgRadius = pics.length ? (parseFloat(getComputedStyle(pics[0]).borderRadius) || 0) : 0;
    const curImgShadow = pics.length ? (getComputedStyle(pics[0]).boxShadow || 'none') : 'none';

    // Target slide values (from the original state we captured on open)
    const targetPadTop = origPadTop.current || getTopMargin();
    const targetPadLR = origPadLR.current || SIDE_MARGIN;
    const targetShadow = origShadow.current || 'none';

    // ── Phase 1: Lock ALL current values (transition: none) ──
    img.style.willChange = 'height, width, margin, border-radius';
    img.style.transition = 'none';
    setImgState(img, cur.h, cur.w, cur.mt, cur.mb, cur.ml, cur.mr, cur.radius);

    if (slide) {
      slide.style.transition = 'none';
      slide.style.paddingTop = curPadTop + 'px';
      slide.style.paddingLeft = curPadLR + 'px';
      slide.style.paddingRight = curPadLR + 'px';
    }
    pics.forEach(pic => {
      pic.style.transition = 'none';
      pic.style.borderRadius = curImgRadius + 'px';
      pic.style.boxShadow = curImgShadow;
    });

    if (p) {
      p.style.position = 'absolute';
      p.style.bottom = '0';
      p.style.left = '0';
      p.style.right = '0';
      p.style.top = 'auto';
      p.style.maxHeight = 'none';
      p.style.height = curPanelH + 'px';
      p.style.opacity = '1';
      p.style.overflow = 'hidden';
      p.style.willChange = 'height, opacity';
      p.style.transition = 'none';
    }

    if (ts) {
      ts.style.overflow = 'hidden';
      ts.style.minHeight = '0';
      ts.style.height = '0px';
      ts.style.opacity = '0';
      ts.style.transition = 'none';
    }
    if (sp) {
      sp.style.height = '0px';
      sp.style.transition = 'none';
    }

    // ── Phase 2: Single reflow — all current values are committed ──
    void img.offsetHeight;

    // ── Phase 3: Set ALL transitions + ALL target values (same frame) ──
    img.style.transition = IMG_TRANSITION;
    setImgState(img, fullH, containerW, 0, 0, 0, 0, 0);

    if (slide) {
      slide.style.transition = SLIDE_TRANSITION;
      slide.style.paddingTop = targetPadTop + 'px';
      slide.style.paddingLeft = targetPadLR + 'px';
      slide.style.paddingRight = targetPadLR + 'px';
    }
    pics.forEach(pic => {
      pic.style.transition = INNER_TRANSITION;
      pic.style.borderRadius = IMG_RADIUS + 'px';
      pic.style.boxShadow = targetShadow;
    });

    if (p) {
      p.style.transition = `height ${DUR}ms ${EASE}, opacity 200ms ${EASE}`;
      p.style.height = '0px';
      p.style.opacity = '0';
    }

    if (ts) {
      ts.style.transition = `height ${DUR}ms ${EASE}, min-height ${DUR}ms ${EASE}, opacity ${DUR}ms ${EASE}`;
      ts.style.height = '72px';
      ts.style.minHeight = '72px';
      ts.style.opacity = '1';
    }
    if (sp) {
      sp.style.transition = `height ${DUR}ms ${EASE}`;
      sp.style.height = '88px';
    }

    // Snap carousel back if it was pulled down
    if (c) {
      c.style.transition = `transform ${DUR}ms ${EASE}, opacity ${DUR}ms ${EASE}`;
      c.style.opacity = '1';
      c.style.transform = 'translateY(0)';
    }

    // 7. Completion
    onceTransitionEnd(img, 'height', DUR, () => {
      flushSync(() => setPanel(false));
      clearAll(p, img, ts, sp, c);
      clearSlideInner(slide, pics);
      busy.current = false;
    });
  }, [panelRef, carouselExtraRef, imageContainerRef, setPanel, getFullImgH, getTopMargin, getExtras, getSlideInner, clearAll, clearSlideInner, onceTransitionEnd, readImgState, setImgState, setInnerState, IMG_TRANSITION, INNER_TRANSITION, SLIDE_TRANSITION]);

  // ── Touch handlers ──

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!enabled || busy.current || e.touches.length !== 1) return;
    dragStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    dragDelta.current = 0;
    dragging.current = false;
    decided.current = false;
    direction.current = null;
    inScrollable.current = !!findScroller(e.target);

    // Capture image height for open gesture
    const img = imageContainerRef.current;
    if (img && !actionPanelOpen) imgStartH.current = img.offsetHeight;

    // Capture original slide/image values for morph tracking
    if (!actionPanelOpen) {
      const { slide, pics } = getSlideInner();
      if (slide) {
        const scs = getComputedStyle(slide);
        origPadTop.current = parseFloat(scs.paddingTop) || 0;
        origPadLR.current = parseFloat(scs.paddingLeft) || 0;
      }
      if (pics.length) {
        origShadow.current = getComputedStyle(pics[0]).boxShadow || 'none';
      }
    }
  }, [enabled, findScroller, imageContainerRef, actionPanelOpen, getSlideInner]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!enabled || busy.current || !dragStart.current || e.touches.length !== 1) return;

    const dx = e.touches[0].clientX - dragStart.current.x;
    const dy = e.touches[0].clientY - dragStart.current.y;
    const ax = Math.abs(dx), ay = Math.abs(dy);

    if (!decided.current) {
      if (Math.sqrt(ax * ax + ay * ay) < DIR_TH) return;
      decided.current = true;
      if (ax > ay * 1.2) { dragging.current = false; return; }
      if (ay > ax * 1.2) {
        const dir = dy > 0 ? 'down' : 'up';
        if (inScrollable.current) {
          const sc = findScroller(e.target);
          if (sc) {
            const can = dir === 'down' ? sc.scrollTop > 0 : sc.scrollTop + sc.clientHeight < sc.scrollHeight - 1;
            if (can) { dragging.current = false; return; }
          }
        }
        dragging.current = true;
        direction.current = dir as 'down' | 'up';
      } else { dragging.current = false; return; }
    }
    if (!dragging.current) return;

    const el = carouselRef.current;
    const p = panelRef.current;
    const img = imageContainerRef.current;
    const containerW = carouselRef.current?.offsetWidth ?? window.innerWidth;

    // ── DOWN: panel closed → drag to close modal ──
    if (direction.current === 'down' && !actionPanelOpen) {
      const pull = Math.max(0, dy);
      dragDelta.current = pull;
      if (el) {
        el.style.transition = 'none';
        el.style.opacity = String(Math.max(0.4, 1 - pull / 250));
        el.style.transform = `translateY(${pull}px)`;
      }
    }
    // ── DOWN: panel open → drag to close panel (reverse of open) ──
    else if (direction.current === 'down' && actionPanelOpen) {
      const pull = Math.max(0, dy);
      dragDelta.current = pull;

      const targetH = getTargetH();
      const topM = getTopMargin();
      const fullH = getFullImgH();
      const panelH = getPanelTargetH();
      const progress = Math.min(1, pull / panelH); // 0 = fully open, 1 = fully closed

      // Image container: grow from cropped → full, remove margins
      if (img) {
        const h = targetH + progress * (fullH - targetH);
        const w = (containerW - SIDE_MARGIN * 2) + progress * SIDE_MARGIN * 2;
        const mt = topM * (1 - progress);
        const mb = SIDE_MARGIN * (1 - progress);
        const ml = SIDE_MARGIN * (1 - progress);
        const mr = SIDE_MARGIN * (1 - progress);
        const r = RADIUS * (1 - progress);
        img.style.transition = 'none';
        setImgState(img, h, w, mt, mb, ml, mr, r);
      }

      // Slide inner: interpolate padding and image styles
      const { slide, pics } = getSlideInner();
      const targetPadTop = origPadTop.current || getTopMargin();
      const targetPadLR = origPadLR.current || SIDE_MARGIN;
      if (slide) {
        slide.style.transition = 'none';
        slide.style.paddingTop = (targetPadTop * progress) + 'px';
        slide.style.paddingLeft = (targetPadLR * progress) + 'px';
        slide.style.paddingRight = (targetPadLR * progress) + 'px';
      }
      pics.forEach(pic => {
        pic.style.transition = 'none';
        pic.style.borderRadius = (IMG_RADIUS * progress) + 'px';
        // Shadow: interpolate via opacity-like approach (none at 0, full at 1)
        pic.style.boxShadow = progress > 0.5 ? origShadow.current : 'none';
      });

      // Panel: shrink
      if (p) {
        p.style.transition = 'none';
        p.style.position = 'absolute';
        p.style.bottom = '0';
        p.style.left = '0';
        p.style.right = '0';
        p.style.top = 'auto';
        p.style.maxHeight = 'none';
        p.style.height = `${panelH * (1 - progress)}px`;
        p.style.opacity = String(Math.max(0, 1 - progress * 1.5));
        p.style.overflow = 'hidden';
      }

      // Thumbnails: restore proportionally
      const { ts, sp } = getExtras();
      if (ts) {
        ts.style.transition = 'none';
        ts.style.overflow = 'hidden';
        ts.style.minHeight = '0';
        ts.style.height = `${Math.round(72 * progress)}px`;
        ts.style.opacity = String(Math.min(1, progress * 2));
      }
      if (sp) {
        sp.style.transition = 'none';
        sp.style.height = `${Math.round(88 * progress)}px`;
      }
    }
    // ── UP: panel closed → drag to open panel ──
    else if (direction.current === 'up' && !actionPanelOpen) {
      const pull = Math.max(0, -dy);
      dragDelta.current = pull;

      const targetH = getTargetH();
      const topM = getTopMargin();
      const maxPanelH = getPanelTargetH();
      const progress = Math.min(1, pull / maxPanelH);
      const panelH = progress * maxPanelH;

      // Panel: absolute bottom, growing
      if (p) {
        p.style.transition = 'none';
        p.style.position = 'absolute';
        p.style.bottom = '0';
        p.style.left = '0';
        p.style.right = '0';
        p.style.top = 'auto';
        p.style.maxHeight = 'none';
        p.style.height = `${panelH}px`;
        p.style.opacity = String(Math.min(1, progress * 1.5));
        p.style.overflow = 'hidden';
      }

      // Image container: shrink toward 21:9 + add margins + border-radius
      if (img && imgStartH.current > 0) {
        const newH = imgStartH.current - progress * (imgStartH.current - targetH);
        const w = containerW - progress * SIDE_MARGIN * 2;
        const mt = topM * progress;
        const mb = SIDE_MARGIN * progress;
        const ml = SIDE_MARGIN * progress;
        const mr = SIDE_MARGIN * progress;
        const r = RADIUS * progress;
        img.style.transition = 'none';
        setImgState(img, Math.max(targetH, newH), w, mt, mb, ml, mr, r);
      }

      // Slide inner: interpolate padding and image styles
      const { slide, pics } = getSlideInner();
      const startPadTop = origPadTop.current || getTopMargin();
      const startPadLR = origPadLR.current || SIDE_MARGIN;
      if (slide) {
        slide.style.transition = 'none';
        slide.style.paddingTop = (startPadTop * (1 - progress)) + 'px';
        slide.style.paddingLeft = (startPadLR * (1 - progress)) + 'px';
        slide.style.paddingRight = (startPadLR * (1 - progress)) + 'px';
      }
      pics.forEach(pic => {
        pic.style.transition = 'none';
        pic.style.borderRadius = (IMG_RADIUS * (1 - progress)) + 'px';
        pic.style.boxShadow = progress > 0.5 ? 'none' : origShadow.current;
      });

      // Thumbnails & spacer → shrink
      const { ts, sp } = getExtras();
      if (ts) {
        ts.style.transition = 'none';
        ts.style.overflow = 'hidden';
        ts.style.minHeight = '0';
        ts.style.height = `${Math.round(72 * (1 - progress))}px`;
        ts.style.opacity = String(Math.max(0, 1 - progress * 2));
      }
      if (sp) {
        sp.style.transition = 'none';
        sp.style.height = `${Math.round(88 * (1 - progress))}px`;
      }
    }
  }, [enabled, actionPanelOpen, panelRef, imageContainerRef, findScroller, getTargetH, getTopMargin, getFullImgH, getPanelTargetH, getExtras, getSlideInner, setImgState]);

  const handleTouchEnd = useCallback(() => {
    if (!dragging.current) {
      dragStart.current = null;
      decided.current = false;
      direction.current = null;
      return;
    }

    const el = carouselRef.current;
    const d = dragDelta.current;
    const p = panelRef.current;
    const img = imageContainerRef.current;

    // ── DOWN: panel closed → close modal or snap back ──
    if (direction.current === 'down' && !actionPanelOpen) {
      if (d >= CLOSE_TH) {
        if (el) {
          void el.offsetHeight;
          el.style.transition = `transform 200ms ${EASE}, opacity 200ms ${EASE}`;
          el.style.opacity = '0';
          el.style.transform = `translateY(${Math.max(d, 200)}px)`;
        }
        setTimeout(onClose, 220);
      } else {
        if (el) {
          void el.offsetHeight;
          el.style.transition = `transform 300ms ${EASE}, opacity 300ms ${EASE}`;
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
        }
      }
    }
    // ── DOWN: panel open → close panel or snap back to open ──
    else if (direction.current === 'down' && actionPanelOpen) {
      if (d >= PANEL_TH) {
        animateClose();
      } else {
        animateOpen();
      }
    }
    // ── UP: panel closed → open panel or snap back ──
    else if (direction.current === 'up' && !actionPanelOpen) {
      if (d >= PANEL_TH) {
        animateOpen();
      } else {
        // Snap back to closed
        const { ts, sp } = getExtras();
        const { slide, pics } = getSlideInner();

        if (p) {
          void p.offsetHeight;
          p.style.transition = `height 250ms ${EASE}, opacity 200ms ${EASE}`;
          p.style.height = '0px';
          p.style.opacity = '0';
        }
        if (img) {
          void img.offsetHeight;
          img.style.transition = `height 250ms ${EASE}, width 250ms ${EASE}, margin 250ms ${EASE}, border-radius 250ms ${EASE}`;
          const containerW = carouselRef.current?.offsetWidth ?? window.innerWidth;
          setImgState(img, imgStartH.current, containerW, 0, 0, 0, 0, 0);
        }
        // Snap slide inner back
        if (slide) {
          void slide.offsetHeight;
          slide.style.transition = `padding 250ms ${EASE}`;
          slide.style.paddingTop = (origPadTop.current || getTopMargin()) + 'px';
          slide.style.paddingLeft = (origPadLR.current || SIDE_MARGIN) + 'px';
          slide.style.paddingRight = (origPadLR.current || SIDE_MARGIN) + 'px';
        }
        pics.forEach(pic => {
          void pic.offsetHeight;
          pic.style.transition = `border-radius 250ms ${EASE}, box-shadow 250ms ${EASE}`;
          pic.style.borderRadius = IMG_RADIUS + 'px';
          pic.style.boxShadow = origShadow.current;
        });
        if (ts) {
          void ts.offsetHeight;
          ts.style.transition = `height 250ms ${EASE}, min-height 250ms ${EASE}, opacity 200ms ${EASE}`;
          ts.style.height = '72px';
          ts.style.minHeight = '72px';
          ts.style.opacity = '1';
        }
        if (sp) {
          void sp.offsetHeight;
          sp.style.transition = `height 250ms ${EASE}`;
          sp.style.height = '88px';
        }
        setTimeout(() => {
          clearAll(p, img, ts, sp, null);
          clearSlideInner(slide, pics);
        }, 270);
      }
    }

    dragging.current = false;
    dragStart.current = null;
    dragDelta.current = 0;
    decided.current = false;
    direction.current = null;
  }, [onClose, actionPanelOpen, panelRef, imageContainerRef, animateOpen, animateClose, getExtras, getSlideInner, getTopMargin, clearAll, clearSlideInner, setImgState]);

  useEffect(() => {
    if (!enabled) return;
    const el = carouselRef.current;
    if (!el) return;
    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: true });
    el.addEventListener('touchend', handleTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enabled, handleTouchStart, handleTouchMove, handleTouchEnd]);

  return { pullRef: carouselRef, isDragging: dragging, animateOpen, animateClose };
}
